import { z } from 'zod';
import { CountryCodeSchema, type CountryCode } from './geo';

/* ------------------------------------------------------------------ */
/* Legal store (W21)                                                   */
/*                                                                     */
/* Terms, privacy, refund + cancellation, restaurant and rider terms   */
/* and the cookie notice, per country (IN / NP) and language           */
/* (EN + HI for India, EN + NE for Nepal). Every document is versioned: */
/* a draft is edited, then published; publishing retires the previous  */
/* published version of the same document, country and language. A    */
/* material change ("ask everyone to accept again") makes the Customer, */
/* Partner and Rider apps show a blocking accept sheet.                */
/* ------------------------------------------------------------------ */

export const LEGAL_DOC_KEYS = [
  'terms_customer',
  'privacy',
  'refund_cancellation',
  'terms_restaurant',
  'terms_rider',
  'cookie',
] as const;
export const LegalDocKeySchema = z.enum(LEGAL_DOC_KEYS);
export type LegalDocKey = z.infer<typeof LegalDocKeySchema>;

export const LEGAL_LOCALES = ['en', 'hi', 'ne'] as const;
export const LegalLocaleSchema = z.enum(LEGAL_LOCALES);
export type LegalLocale = z.infer<typeof LegalLocaleSchema>;

/** India documents exist in English and Hindi; Nepal documents in English and Nepali. */
export const LEGAL_LOCALES_BY_COUNTRY: Record<CountryCode, readonly LegalLocale[]> = {
  IN: ['en', 'hi'],
  NP: ['en', 'ne'],
};

export const LEGAL_STATUSES = ['draft', 'published', 'retired'] as const;
export const LegalStatusSchema = z.enum(LEGAL_STATUSES);
export type LegalStatus = z.infer<typeof LegalStatusSchema>;

/** The app realms that accept documents (Partner agreement signing stays in W4). */
export const LEGAL_REALMS = ['customer', 'partner', 'rider'] as const;
export const LegalRealmSchema = z.enum(LEGAL_REALMS);
export type LegalRealm = z.infer<typeof LegalRealmSchema>;

/** Documents each app asks its people to accept. */
export const LEGAL_DOCS_BY_REALM: Record<LegalRealm, readonly LegalDocKey[]> = {
  customer: ['terms_customer', 'privacy', 'refund_cancellation'],
  partner: ['terms_restaurant', 'privacy'],
  rider: ['terms_rider', 'privacy'],
};

/**
 * Every body that has not been reviewed by lawyers starts with this line (enforced on publish).
 * Capsico has not had any document reviewed yet, so every document carries it.
 */
export const LEGAL_DRAFT_BANNER = 'Draft — pending legal review';

export const LEGAL_DOC_LABELS: Record<LegalDocKey, string> = {
  terms_customer: 'Terms of use (customers)',
  privacy: 'Privacy policy',
  refund_cancellation: 'Refund and cancellation policy',
  terms_restaurant: 'Restaurant partner terms',
  terms_rider: 'Delivery partner (rider) terms',
  cookie: 'Cookie notice',
};

export const LEGAL_LOCALE_LABELS: Record<LegalLocale, string> = {
  en: 'English',
  hi: 'हिन्दी',
  ne: 'नेपाली',
};

/** URL slugs on the public site: /legal/<slug>. */
export const LEGAL_DOC_SLUGS: Record<LegalDocKey, string> = {
  terms_customer: 'terms',
  privacy: 'privacy',
  refund_cancellation: 'refunds',
  terms_restaurant: 'restaurant-terms',
  terms_rider: 'rider-terms',
  cookie: 'cookies',
};

export function legalDocKeyFromSlug(slug: string): LegalDocKey | null {
  const entry = (Object.entries(LEGAL_DOC_SLUGS) as Array<[LegalDocKey, string]>).find(
    ([, value]) => value === slug,
  );
  return entry ? entry[0] : null;
}

export function isLocaleOfCountry(country: CountryCode, locale: LegalLocale): boolean {
  return LEGAL_LOCALES_BY_COUNTRY[country].includes(locale);
}

/** Longest body accepted (markdown). */
export const LEGAL_BODY_MAX = 200_000;

const TitleSchema = z.string().trim().min(3, 'Give the document a title').max(140);
const BodySchema = z
  .string()
  .min(20, 'The document is too short')
  .max(LEGAL_BODY_MAX, 'The document is too long');
const SummarySchema = z.string().trim().max(1000);

/* ---------------- documents ---------------- */

export const LegalDocumentSummarySchema = z.object({
  id: z.uuid(),
  key: LegalDocKeySchema,
  country: CountryCodeSchema,
  locale: LegalLocaleSchema,
  version: z.number().int().min(1),
  status: LegalStatusSchema,
  title: z.string(),
  summaryOfChanges: z.string().nullable(),
  requiresReconsent: z.boolean(),
  /** Since when people must have accepted (a material publish); null = no accept sheet. */
  consentRequiredSince: z.string().nullable(),
  /** Always false today: no document has been reviewed by lawyers. */
  reviewed: z.boolean(),
  publishedAt: z.string().nullable(),
  publishedBy: z.string().nullable(),
  retiredAt: z.string().nullable(),
  updatedAt: z.string(),
  updatedBy: z.string(),
  /** Row version for edits (expectedVersion). */
  rowVersion: z.number().int().min(1),
});
export type LegalDocumentSummary = z.infer<typeof LegalDocumentSummarySchema>;

export const LegalDocumentSchema = LegalDocumentSummarySchema.extend({ body: z.string() });
export type LegalDocument = z.infer<typeof LegalDocumentSchema>;

export const LegalAdminListQuerySchema = z.object({
  country: CountryCodeSchema.optional(),
  key: LegalDocKeySchema.optional(),
  locale: LegalLocaleSchema.optional(),
});
export type LegalAdminListQuery = z.infer<typeof LegalAdminListQuerySchema>;

/** One row of the Capsico OS documents list: a document in one country + language. */
export const LegalDocumentGroupSchema = z.object({
  key: LegalDocKeySchema,
  country: CountryCodeSchema,
  locale: LegalLocaleSchema,
  published: LegalDocumentSummarySchema.nullable(),
  draft: LegalDocumentSummarySchema.nullable(),
  versions: z.array(LegalDocumentSummarySchema),
});
export type LegalDocumentGroup = z.infer<typeof LegalDocumentGroupSchema>;

export const LegalAdminListSchema = z.object({ groups: z.array(LegalDocumentGroupSchema) });
export type LegalAdminList = z.infer<typeof LegalAdminListSchema>;

export const CreateLegalDraftInputSchema = z.object({
  key: LegalDocKeySchema,
  country: CountryCodeSchema,
  locale: LegalLocaleSchema,
  title: TitleSchema,
  body: BodySchema,
  summaryOfChanges: SummarySchema.optional(),
});
export type CreateLegalDraftInput = z.infer<typeof CreateLegalDraftInputSchema>;

export const UpdateLegalDraftInputSchema = z.object({
  title: TitleSchema,
  body: BodySchema,
  summaryOfChanges: SummarySchema.optional(),
  expectedVersion: z.number().int().min(1),
});
export type UpdateLegalDraftInput = z.infer<typeof UpdateLegalDraftInputSchema>;

export const PublishLegalDocInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  /** Shown in the version history and in the accept sheet ("What changed"). */
  reason: z.string().trim().min(5, 'Say what changed (at least 5 characters)').max(1000),
  /** Material change: everyone must accept this version before using the app again. */
  requiresReconsent: z.boolean(),
});
export type PublishLegalDocInput = z.infer<typeof PublishLegalDocInputSchema>;

export const DiscardLegalDraftInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
});
export type DiscardLegalDraftInput = z.infer<typeof DiscardLegalDraftInputSchema>;

export const LegalDiffQuerySchema = z.object({ against: z.uuid().optional() });
export type LegalDiffQuery = z.infer<typeof LegalDiffQuerySchema>;

export const LegalDiffLineSchema = z.object({
  kind: z.enum(['same', 'added', 'removed']),
  text: z.string(),
});
export type LegalDiffLine = z.infer<typeof LegalDiffLineSchema>;

export const LegalDiffSchema = z.object({
  from: LegalDocumentSummarySchema.nullable(),
  to: LegalDocumentSummarySchema,
  lines: z.array(LegalDiffLineSchema),
  added: z.number().int(),
  removed: z.number().int(),
  /** True when the documents were too long to compare line by line. */
  truncated: z.boolean(),
});
export type LegalDiff = z.infer<typeof LegalDiffSchema>;

export const LegalStatsQuerySchema = z.object({
  key: LegalDocKeySchema,
  country: CountryCodeSchema,
});
export type LegalStatsQuery = z.infer<typeof LegalStatsQuerySchema>;

export const LegalVersionStatsSchema = z.object({
  documentId: z.uuid(),
  locale: LegalLocaleSchema,
  version: z.number().int(),
  status: LegalStatusSchema,
  publishedAt: z.string().nullable(),
  requiresReconsent: z.boolean(),
  acceptances: z.object({
    customer: z.number().int(),
    partner: z.number().int(),
    rider: z.number().int(),
    total: z.number().int(),
  }),
});
export type LegalVersionStats = z.infer<typeof LegalVersionStatsSchema>;

export const LegalStatsSchema = z.object({
  key: LegalDocKeySchema,
  country: CountryCodeSchema,
  versions: z.array(LegalVersionStatsSchema),
});
export type LegalStats = z.infer<typeof LegalStatsSchema>;

/* ---------------- reconsent (apps) ---------------- */

export const LegalPendingQuerySchema = z.object({
  /** The app language; documents fall back to English when not published in it. */
  locale: LegalLocaleSchema.optional(),
});
export type LegalPendingQuery = z.infer<typeof LegalPendingQuerySchema>;

export const LegalPendingDocSchema = z.object({
  documentId: z.uuid(),
  key: LegalDocKeySchema,
  country: CountryCodeSchema,
  locale: LegalLocaleSchema,
  version: z.number().int(),
  title: z.string(),
  summaryOfChanges: z.string().nullable(),
  body: z.string(),
  publishedAt: z.string().nullable(),
});
export type LegalPendingDoc = z.infer<typeof LegalPendingDocSchema>;

export const LegalPendingSchema = z.object({
  country: CountryCodeSchema,
  pending: z.array(LegalPendingDocSchema),
});
export type LegalPending = z.infer<typeof LegalPendingSchema>;

export const LegalAcceptInputSchema = z.object({
  documentIds: z.array(z.uuid()).min(1).max(10),
});
export type LegalAcceptInput = z.infer<typeof LegalAcceptInputSchema>;

export const LegalAcceptResultSchema = z.object({
  accepted: z.number().int(),
  remaining: z.number().int(),
});
export type LegalAcceptResult = z.infer<typeof LegalAcceptResultSchema>;

/* ---------------- public (site + apps) ---------------- */

export const PublicLegalQuerySchema = z.object({
  country: CountryCodeSchema.default('IN'),
  lang: LegalLocaleSchema.optional(),
});
export type PublicLegalQuery = z.infer<typeof PublicLegalQuerySchema>;

export const PublicLegalDocSchema = z.object({
  key: LegalDocKeySchema,
  slug: z.string(),
  country: CountryCodeSchema,
  locale: LegalLocaleSchema,
  /** Languages this document is published in, for hreflang links. */
  locales: z.array(LegalLocaleSchema),
  version: z.number().int(),
  title: z.string(),
  body: z.string(),
  publishedAt: z.string().nullable(),
  reviewed: z.boolean(),
});
export type PublicLegalDoc = z.infer<typeof PublicLegalDocSchema>;

export const PublicLegalIndexSchema = z.object({
  country: CountryCodeSchema,
  docs: z.array(
    z.object({
      key: LegalDocKeySchema,
      slug: z.string(),
      title: z.string(),
      locales: z.array(LegalLocaleSchema),
      publishedAt: z.string().nullable(),
    }),
  ),
});
export type PublicLegalIndex = z.infer<typeof PublicLegalIndexSchema>;

/** Grievance officer (India: IT Rules + DPDP Act; Nepal: Consumer Protection + Privacy Act). */
export const GrievanceOfficerSchema = z.object({
  country: CountryCodeSchema,
  name: z.string(),
  email: z.string(),
  phone: z.string(),
  address: z.string(),
  /** True while any field still reads "To be appointed". */
  placeholder: z.boolean(),
});
export type GrievanceOfficer = z.infer<typeof GrievanceOfficerSchema>;

export const GRIEVANCE_PLACEHOLDER = 'To be appointed';

/** Settings keys holding the grievance officer (per country, Capsico OS › Settings › Legal). */
export const GRIEVANCE_CONFIG_KEYS = {
  name: 'legal.grievance_name',
  email: 'legal.grievance_email',
  phone: 'legal.grievance_phone',
  address: 'legal.grievance_address',
} as const;
